import { CKB } from '@did/constants/chain'
import {
  AccountInput,
  Button,
  Dialog,
  TextInput
} from '@did/das-balance-components'
import errno from '@did/constants/errno'
import { useDasBalanceContext, useConfigVal } from '@did/das-app-context'
import { SendContentType } from '@did/das-balance-types'
import { addressValidate, expandUnit, thousandSplit } from '@did/tools'
import React, { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import styles from './styles.module.scss'
import { ORDER_ACTION_TYPE } from '@did/types'
interface IFormValues {
  sendToCKBAddress: {
    value: string
    address: string
  }
  amount: string
}

export const SendContent: SendContentType = (param) => {
  const { tt, connectedAccount, services, alert, walletSdk, isProd } =
    useDasBalanceContext()
  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
    setValue,
    getValues
  } = useForm<IFormValues>({
    mode: 'all'
  })
  const [withdrawConfirmLoading, setWithdrawConfirmLoading] = useState(false)
  const [sendTipsShowing, setSendTipsShowing] = useState(false)

  const { balance, successCallback } = param

  const config = useConfigVal({ services })

  useEffect(() => {
    register('sendToCKBAddress', {
      validate: (val, formVal) => {
        if (addressValidate(val?.address, CKB.symbol, isProd)) {
          return true
        }
        return tt('{chain} address format is wrong', {
          chain: CKB.symbol.toUpperCase()
        })
      }
    })
  }, [])

  const onCloseSendTipsDialog = () => {
    setSendTipsShowing(false)
  }

  const onWithdrawConfirm = async (param: IFormValues) => {
    if (!connectedAccount) return
    setWithdrawConfirmLoading(true)
    const { signTxList, onFailed } = await walletSdk?.initSignContext()

    try {
      const res = await services.account.availableBalanceWithdraw({
        key_info: {
          coin_type: connectedAccount.chain?.coinType,
          key: connectedAccount.address
        },
        evm_chain_id: connectedAccount.computedChainId,
        receiver_address: param.sendToCKBAddress.address,
        amount: expandUnit(param.amount, CKB.decimals),
        withdraw_all: String(param.amount) === balance
      })

      if (!res) {
        return
      }

      const signatureList = await signTxList(res)

      const { hash } = await services.account.sendTrx(signatureList)
      successCallback?.({
        txHashLink: `${CKB.getExplorerTrx(isProd)}${hash}`,
        txHash: hash,
        actionType: ORDER_ACTION_TYPE.WITHDRAW_FROM_WALLET
      })
    } catch (err: any) {
      onFailed?.()
      if (
        ![
          errno.metaMaskUserRejectedAccountAccess,
          errno.metaMaskUserDeniedMessageSignature
        ].includes(err.code) &&
        err !== errno.tronLinkConfirmationDeclinedByUser &&
        err.message !== errno.walletConnectUserRejectedTheTransaction
      ) {
        if (
          err.code === errno.metaMaskWalletRequestPermissions ||
          err.code === errno.tronLinkAuthorizationRequestsAreBeingProcessed
        ) {
          alert({
            title: tt('Tips'),
            message: tt(
              'Other requests for the wallet are not processed, please try again after processing'
            )
          })
        } else if (err.message === errno.coinbaseWalletUsingMultipleWallet) {
          alert({
            title: tt('Tips'),
            message: tt(
              'Please check if you are using multiple wallet plugins. Please disable multiple wallet plugins, keep only one wallet plugin and try again.'
            )
          })
        } else if (err.code === errno.apiErrorCodeNotSupportFullAddress) {
          alert({
            title: tt('Tips'),
            message: tt('CKB address in long address format is not supported.')
          })
        } else if (err.code === errno.apiErrorCodeRejectedOutPoint) {
          alert({
            title: tt('Tips'),
            message: tt(
              'Frequent operations. There are still transactions being processed in your wallet address, please try again after 30s.'
            )
          })
        } else if (err.code === errno.apiErrorCodeInsufficientBalance) {
          alert({
            title: tt('Insufficient balance'),
            message: tt('Insufficient balance. Please check your balance.')
          })
        } else if (err.code === errno.apiErrorCodeNotEnoughChange) {
          setSendTipsShowing(true)
        } else {
          alert({
            title: tt('Error'),
            message: err.code ? `${err.code}: ${err.message}` : `${err}`
          })
        }
      }
    } finally {
      setWithdrawConfirmLoading(false)
    }
  }

  const onInputSendTo = (value: string) => {
    // Handle input send to
  }

  const onFillBalance = () => {
    setValue('amount', balance)
  }

  const onSendAll = async () => {
    onFillBalance()
    const data = getValues()
    setSendTipsShowing(false)
    await onWithdrawConfirm({ ...data, amount: balance })
  }

  const amountInputVailRegister = useMemo(() => {
    return register('amount', {
      min: {
        value: Number(config.min_change_capacity),
        message: tt(
          '{fieldName} must be greater than or equal to {min} {unit}',
          {
            fieldName: tt('Amount'),
            min: config.min_change_capacity,
            unit: 'CKB'
          }
        )
      },
      max: {
        value: balance,
        message: tt('{fieldName} must be less than or equal to {max} {unit}', {
          fieldName: tt('Amount'),
          max: balance,
          unit: 'CKB'
        })
      },
      required: {
        value: true,
        message: tt('Invalid {fieldName}', { fieldName: tt('Amount') })
      }
    })
  }, [balance, config.min_change_capacity])

  return (
    <div>
      <form onSubmit={handleSubmit(onWithdrawConfirm)}>
        <label className={styles.availableBalanceWithdrawLabel}>
          {tt('Send to')}
        </label>
        <AccountInput
          chain={CKB}
          name="Send to"
          placeholder={tt('DAS or {symbol} address', { symbol: CKB.symbol })}
          errorMessages={[errors?.sendToCKBAddress?.message || ''].filter(
            Boolean
          )}
          onInput={onInputSendTo}
          onChange={(val) => {
            setValue('sendToCKBAddress', val)
            clearErrors('sendToCKBAddress')
          }}
        />
        <label
          className={`${styles.availableBalanceWithdrawLabelBalance} ${styles.availableBalanceWithdrawLabel}`}
        >
          {tt('Amount')}
          <span
            className={styles.availableBalanceWithdrawBalance}
            onClick={onFillBalance}
          >
            {tt('Balance: ')}
            <span className={styles.availableBalanceWithdrawBalanceValue}>
              {thousandSplit(balance)}
            </span>
            <span> CKB</span>
          </span>
        </label>
        <TextInput
          type="number"
          inputMode="numeric"
          suffix="CKB"
          step="0.00000001"
          placeholder={tt('At least {minChangeCapacity} CKB', {
            minChangeCapacity: config.min_change_capacity
          })}
          errorMessages={[errors?.amount?.message || ''].filter(Boolean)}
          {...amountInputVailRegister}
        />
        <div className={styles.availableBalanceWithdrawAction}>
          <Button
            type="submit"
            black
            large
            block
            isLoadingGradient={false}
            loading={withdrawConfirmLoading}
          >
            {!withdrawConfirmLoading && tt('Send')}
          </Button>
        </div>
      </form>
      <Dialog
        showing={sendTipsShowing}
        title="Tips"
        className={styles.availableBalanceWithdrawTips}
        closeButton
        onClose={onCloseSendTipsDialog}
        action={
          <div className={styles.availableBalanceWithdrawTipsAction}>
            <Button black block large onClick={onSendAll}>
              {tt('Send all')}
            </Button>
            <Button large block onClick={onCloseSendTipsDialog}>
              {tt('Cancel')}
            </Button>
          </div>
        }
      >
        <div className={styles.availableBalanceWithdrawTipsContent}>
          {tt(
            'DAS is a smart contract that runs on the Nervos. Due to the underlying logic of the contract, the remaining amount is too low (less than {minChangeCapacity} CKB) to send a transaction. Do you want to send all {availableBalance} CKB?',
            {
              availableBalance: thousandSplit(balance),
              minChangeCapacity: config.min_change_capacity
            }
          )}
        </div>
      </Dialog>
    </div>
  )
}
